import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import type { AutoPayScheduleReviewComponentType } from '@/src/experiences/card-payments/screens/AutoPayScheduleReview'
import { SchedulePaymentsApi } from '@/src/experiences/card-payments/store/api'
import { formatRecurringPaymentStrategy } from '@/src/experiences/card-payments/screens/AutoPayReview/PlatformAutoPayReview'
import { formatToTextMonth } from '@/src/common/utils/date'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { formatPaymentProfile } from '@/src/experiences/card-payments/formatPaymentProfile'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import { useLogError } from '@/src/common/hooks/useLogError'
import { routerBack } from '@/src/common/utils/routerBack'

interface PlatformAutoPayScheduleReviewProps {
  AutoPayScheduleReviewComponent: AutoPayScheduleReviewComponentType
}

type CardAccountId = NonNullable<ReturnType<typeof useCardAccount>['data']>[number]['id']

export function PlatformAutoPayScheduleReview({
  AutoPayScheduleReviewComponent,
}: PlatformAutoPayScheduleReviewProps) {
  const query = useCardAccount()
  return query.data?.[0]?.id ? (
    <PlatformAutoPayScheduleReviewForCardAccount
      AutoPayScheduleReviewComponent={AutoPayScheduleReviewComponent}
      cardAccountId={query.data[0].id}
    />
  ) : (
    <AutoPayScheduleReviewComponent loading />
  )
}

function PlatformAutoPayScheduleReviewForCardAccount({
  AutoPayScheduleReviewComponent,
  cardAccountId,
}: PlatformAutoPayScheduleReviewProps & {
  cardAccountId: CardAccountId
}) {
  const Alert = useAlert(PlatformAutoPayScheduleReviewForCardAccount.name)
  const query = SchedulePaymentsApi.endpoints[
    'GET /card/:cardAccountId/payments/schedules'
  ].useQuery({ params: { cardAccountId } })
  const [deletePaymentSchedule] =
    SchedulePaymentsApi.endpoints[
      'DELETE /card/:cardAccountId/payments/schedules/:scheduleId'
    ].useMutation()
  const logError = useLogError()
  const recurringPayment = query.data?.find((schedule) => schedule.type === 'recurring')

  function confirmRemoveAutoPay() {
    const scheduledTransferId = recurringPayment?.id
    if (!scheduledTransferId) {
      return
    }

    Alert.alert(
      'Cancellation Notice',
      'Cancellation must be received by 9:00 AM Eastern on the date your payment is scheduled. Requests received after this will apply to the next scheduled payment.',
      [
        { text: 'Back', onPress: () => {}, style: 'cancel' },
        {
          text: CardPaymentsConstants.Strings.CancelAutoPay,
          onPress() {
            void deletePaymentSchedule({
              params: {
                cardAccountId,
                scheduleId: scheduledTransferId,
              },
            }).then((response) => {
              if (response.error) {
                logError('Failed to delete payment schedule', response.error)
                Alert.alert('Failed to delete payment schedule', 'Please try again later.')
              } else {
                Alert.alert('Payment schedule deleted', 'Your payment schedule has been deleted.')
                void routerBack()
              }
            })
          },

          style: 'destructive',
        },
      ],
    )
  }

  return (
    <AutoPayScheduleReviewComponent
      loading={query.isUninitialized || query.isLoading || query.isFetching}
      {...(recurringPayment
        ? {
            amount: formatRecurringPaymentStrategy(recurringPayment),
            day: recurringPayment.processDateTime
              ? formatToTextMonth(new Date(recurringPayment.processDateTime), {})
              : undefined,
            // todo - support debit cards
            account: formatPaymentProfile(recurringPayment.profile),
            confirmRemoveAutoPay,
          }
        : undefined)}
      confirmRemoveAutoPay={confirmRemoveAutoPay}
    />
  )
}
